import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import BudgetModes from '@/common/enums/budget-modes.enum';
import ReachAndFrequencyStatus from '@/common/enums/reach-and-frequency-status.enum';
import { Campaign, LineItem } from '@/common/models';
import Advertiser from '@/common/models/advertisers/Advertiser';
import { CampaignType } from '@/common/models/campaign/Campaign';
import CampaignOptions from '@/common/models/campaign/CampaignOptions';
import EntityProperty from '@/common/models/EntityProperty';
import Account from '@/common/models/misc-api-models/Account';
import { IOption } from '@/common/models/Option';
import { Filter, FilterComparisonOperator, FilterLogicalOperator } from '@/common/models/query-params';
import { DASH, getStore } from '@/globals';
import { useAccounts, useAdvertisers } from '@/org-app/services/tanstack/queries';
import { useUserOrganizations } from '@/org-app/services/tanstack/queries/userOrganizations.queries';
import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import rootStore from '@/store';
import {
  UPDATE_CAMPAIGN_ACCOUNT_ID,
  UPDATE_CAMPAIGN_ADVERTISER_ID,
  UPDATE_CAMPAIGN_MARGINS,
  UPDATE_CAMPAIGN_MINSECONDSBETWEENPLAYS,
  UPDATE_CAMPAIGN_NAME,
  UPDATE_CAMPAIGN_NOTES,
  UPDATE_CAMPAIGN_PACINGBUDGET,
  UPDATE_CAMPAIGN_PACINGBUDGETMODE,
  UPDATE_CAMPAIGN_PRIORITY,
} from '@/store/modules/models/mutation-types';
import i18n from '@/vueI18n';

export const getEntityProperties = () => {
  const budgetMode = new EntityProperty<LineItem>({
    label: capitalize(i18n.global.t('names.budgetMode', { n: 1 })),
    property: 'pacing_budget_mode',
    type: TableColumnTypes.string,
    source: () => [
      <IOption>{ id: BudgetModes.BUDGET, name: capitalize(i18n.global.t('names.budget')) },
      <IOption>{ id: BudgetModes.IMPRESSIONS, name: capitalize(i18n.global.t('names.impression', { n: 2 })) },
    ],
    editablePropertyName: 'pacingBudgetMode',
  });

  const getExtraValidation = (campaign: Campaign) => campaign.getExtraValidation();
  const action = (campaign: Campaign) =>
    rootStore.dispatch('orgApp/updateCampaign', { instance: campaign, showLoader: false });

  return {
    id: new EntityProperty({
      label: '#',
      property: 'campaign_id',
      type: TableColumnTypes.string,
      editablePropertyName: 'id',
    }),
    name: new EntityProperty({
      label: capitalize(i18n.global.t('names.name', { n: 1 })),
      property: 'name',
      type: TableColumnTypes.string,
      mutation: UPDATE_CAMPAIGN_NAME,
      editableClassName: Campaign.type,
      editablePropertyName: 'name',
    }),
    status: new EntityProperty({
      label: capitalize(i18n.global.t('names.status', { n: 1 })),
      property: 'state',
      type: TableColumnTypes.string,
      source: () =>
        new Promise((resolve) =>
          resolve(CampaignOptions.statusOptions().map((x) => ({ id: x.value, name: x.name, filters: x.filters }))),
        ),
    }),
    account: new EntityProperty({
      label: capitalize(i18n.global.t('names.account', { n: 1 })),
      property: 'account.name',
      type: TableColumnTypes.string,
      source: () => runQueryInScope(useAccounts),
      sourceFilterProperty: 'account_id',
      editablePropertyName: 'accountID',
      mutation: UPDATE_CAMPAIGN_ACCOUNT_ID,
      editableClassName: Campaign.type,
      postMutationCallback: (value: Account, entity: Campaign) => {
        entity.account = value;
      },
      fallbackToString: (entity: Campaign) => {
        return entity.account.name;
      },
    }),
    advertiser: new EntityProperty({
      label: capitalize(i18n.global.t('names.advertiser', { n: 1 })),
      property: 'advertiser.name',
      type: TableColumnTypes.string,
      source: () => runQueryInScope(useAdvertisers),
      sourceFilterProperty: 'advertiser_id',
      editablePropertyName: 'advertiserID',
      mutation: UPDATE_CAMPAIGN_ADVERTISER_ID,
      editableClassName: Campaign.type,
      postMutationCallback: (value: Advertiser, entity: Campaign) => {
        entity.advertiser = value;
      },
      isInlineEditingAvailable: false,
    }),
    pacingVsExpected: new EntityProperty({
      label: capitalize(i18n.global.t('messages.pacingVsExpected')),
      property: 'cachedpacing.pacing',
      type: TableColumnTypes.percentage,
    }),
    pacingVsEven: new EntityProperty({
      label: capitalize(i18n.global.t('messages.pacingVsEven')),
      property: 'cachedpacing.pacing',
      type: TableColumnTypes.percentage,
    }),
    progress: new EntityProperty({
      label: capitalize(i18n.global.t('names.progress', { n: 1 })),
      property: 'cachedpacing.progress',
      type: TableColumnTypes.percentage,
    }),
    startDate: new EntityProperty({
      label: capitalize(i18n.global.t('names.start', { n: 1 })),
      property: 'start_date',
      type: TableColumnTypes.date,
      editablePropertyName: 'startDate',
    }),
    endDate: new EntityProperty({
      label: capitalize(i18n.global.t('names.end', { n: 1 })),
      property: 'end_date',
      type: TableColumnTypes.date,
      editablePropertyName: 'endDate',
    }),
    budget: new EntityProperty({
      label: capitalize(i18n.global.t('names.budget', { n: 1 })),
      property: 'budget',
      type: TableColumnTypes.currency,
      editablePropertyName: 'budget',
    }),
    targetedImpressions: new EntityProperty({
      label: capitalize(i18n.global.t('messages.impressionGoal')),
      property: 'targeted_impressions',
      type: TableColumnTypes.integer,
      editablePropertyName: 'targetedImpressions',
    }),
    totalImpressions: new EntityProperty({
      label: capitalize(i18n.global.t('names.impressionDelivered', { n: 2 })),
      property: 'cachedpacing.total_impressions',
      type: TableColumnTypes.integer,
    }),
    dailyCap: new EntityProperty({
      label: capitalize(i18n.global.t('messages.dailyBudgetCap')),
      property: 'cap',
      type: (c: Campaign) =>
        c.pacingBudgetMode === BudgetModes.BUDGET ? TableColumnTypes.currency : TableColumnTypes.integer,
      editablePropertyName: 'dailyCap',
    }),
    totalSpendOwner: new EntityProperty({
      label: capitalize(i18n.global.t('names.adSpend', { n: 1 })),
      property: 'cachedpacing.total_spend_owner',
      type: TableColumnTypes.currency,
    }),
    averageCPMOwner: new EntityProperty({
      label: capitalize(i18n.global.t('names.cpm', { n: 1 })),
      property: 'cachedpacing.average_cpm_owner',
      type: TableColumnTypes.currency,
    }),
    totalSpendBuyer: new EntityProperty({
      label: capitalize(i18n.global.t('names.adSpend', { n: 1 })),
      property: 'cachedpacing.total_spend_buyer',
      type: TableColumnTypes.currency,
    }),
    averageCPMBuyer: new EntityProperty({
      label: capitalize(i18n.global.t('names.cpm', { n: 1 })),
      property: 'cachedpacing.average_cpm_buyer',
      type: TableColumnTypes.currency,
    }),
    minSecondsBetweenPlays: new EntityProperty({
      label: capitalize(i18n.global.t('names.frequencyCap', { n: 1 })),
      property: 'min_seconds_between_plays',
      editablePropertyName: 'minSecondsBetweenPlays',
      type: TableColumnTypes.integer,
      mutation: UPDATE_CAMPAIGN_MINSECONDSBETWEENPLAYS,
      editableClassName: Campaign.type,
      action: (campaign) => getStore().dispatch('orgApp/updateCampaign', { instance: campaign, showLoader: false }),
    }),
    lastModifiedOn: new EntityProperty({
      label: capitalize(i18n.global.t('messages.lastModifiedOn')),
      property: 'modified_on_utc',
      type: TableColumnTypes.datetime,
    }),
    cachedReportLastUpdatedOn: new EntityProperty({
      label: capitalize(i18n.global.t('messages.statsLastRefreshedOn')),
      property: 'cachedpacing.cached_report_last_updated_utc',
      type: TableColumnTypes.datetime,
    }),
    createdBy: new EntityProperty({
      label: capitalize(i18n.global.t('messages.createdBy')),
      property: 'created_by',
      type: TableColumnTypes.string,
      source: () => runQueryInScope(useUserOrganizations),
      sourceFilterProperty: 'created_by',
      sourceLabel: 'email',
      sourceID: 'userID',
    }),
    notes: new EntityProperty({
      label: capitalize(i18n.global.t('names.notes', { n: 1 })),
      property: 'notes',
      type: TableColumnTypes.string,
      mutation: UPDATE_CAMPAIGN_NOTES,
      editableClassName: Campaign.type,
    }),
    planID: new EntityProperty({
      label: capitalize(i18n.global.t('names.planID', { n: 1 })),
      property: 'plan_id',
      type: TableColumnTypes.integer,
    }),
    reversedPriority: new EntityProperty({
      label: capitalize(i18n.global.t('names.priority', { n: 1 })),
      property: 'priority',
      type: TableColumnTypes.integer,
      source: [1, 2, 3, 4, 5, 6, 7, 8, 9].map((x) => ({
        label: `${x}`,
        value: 10 - x,
      })),
      sourceLabel: 'label',
      sourceID: 'value',
      editablePropertyName: 'priority',
      editableClassName: Campaign.type,
      mutation: UPDATE_CAMPAIGN_PRIORITY,
      action: (campaign: Campaign) =>
        getStore().dispatch('orgApp/updateCampaign', { instance: campaign, showLoader: false }),
      defaultValue: 5,
    }),
    campaignType: new EntityProperty({
      label: capitalize(i18n.global.t('names.campaignType', { n: 1 })),
      property: 'campaign_type',
      type: TableColumnTypes.string,
      source: () =>
        new Promise((resolve) =>
          resolve(
            Object.keys(CampaignType).map((key, i) => ({
              id: i,
              name: capitalize(CampaignType[key].replace('_', ' ').toLowerCase()),
              value: CampaignType[key],
              logicalOperator: FilterLogicalOperator.AND,
              filters: [new Filter('campaign_type', [CampaignType[key]], FilterComparisonOperator.EQUAL)],
            })),
          ),
        ),
    }),
    createdFromPlan: new EntityProperty({
      label: capitalize(i18n.global.t('messages.createdFromPlan')),
      type: TableColumnTypes.string,
    }),
    publicCampaign: new EntityProperty({
      label: capitalize(i18n.global.t('messages.publicCampaign')),
      type: TableColumnTypes.string,
    }),
    testCampaign: new EntityProperty({
      label: capitalize(i18n.global.t('names.testCampaign')),
      property: 'test_campaign',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isTestCampaign',
    }),
    insertionOrderBudget: new EntityProperty({
      label: capitalize(i18n.global.t('names.insertionOrderBudget', { n: 1 })),
      property: 'insertionOrderBudget',
      type: TableColumnTypes.currency,
      editablePropertyName: 'insertionOrderBudget',
    }),
    budgetMode,
    pacingBudget: new EntityProperty({
      label: (c: Campaign) => {
        if (!c?.pacingBudgetMode) return DASH;
        return capitalize(
          c.pacingBudgetMode === BudgetModes.BUDGET
            ? i18n.global.t('names.budget', { n: 1 })
            : i18n.global.t('names.impression', { n: 2 }),
        );
      },
      property: 'pacing_budget',
      type: (c: Campaign) =>
        c.pacingBudgetMode === BudgetModes.BUDGET ? TableColumnTypes.currency : TableColumnTypes.integer,
      editablePropertyName: 'pacingBudget',
      mutation: UPDATE_CAMPAIGN_PACINGBUDGET,
      editableClassName: Campaign.type,
      action,
      getExtraValidation,
      isEditableHandler: (c: Campaign) => c.pacingBudgetMode === BudgetModes.BUDGET,
      preMutationTransform: (x) => (x != null ? Number.parseFloat(String(x).replace(/,/g, '')) : null),
      postMutationCallback: (value, entity) => {
        if (value == null) return;
        rootStore.commit(UPDATE_CAMPAIGN_PACINGBUDGETMODE, {
          lineItem: entity,
          value: BudgetModes.BUDGET,
        });
      },
      mutatesProperties: [budgetMode],
    }),
    targetImpressions: new EntityProperty({
      label: capitalize(i18n.global.t('messages.impressionGoal')),
      property: 'targeted_impressions',
      type: TableColumnTypes.integer,
      mutation: UPDATE_CAMPAIGN_PACINGBUDGET,
      editableClassName: Campaign.type,
      action,
      getExtraValidation,
      isEditableHandler: (c) => c.pacingBudgetMode === BudgetModes.IMPRESSIONS,
      preventEditingEntityProperties: ['budget'],
      preMutationTransform: (x) => (x != null ? Number.parseInt(x, 10) : null),
      postMutationCallback: (value, entity) => {
        if (value == null) return;
        rootStore.commit(UPDATE_CAMPAIGN_PACINGBUDGETMODE, {
          campaign: entity,
          value: BudgetModes.IMPRESSIONS,
        });
      },
      mutatesProperties: [budgetMode],
    }),
    campaignProperties: new EntityProperty({
      label: capitalize(i18n.global.t('names.customField', { n: 2 })),
      property: 'campaign_properties',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'campaignProperties',
      editableClassName: Campaign.type,
      action,
      getExtraValidation,
    }),
    campaignMargins: new EntityProperty({
      label: capitalize(i18n.global.t('names.margin', { n: 2 })),
      property: 'lineitem_margins',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'margins',
      mutation: UPDATE_CAMPAIGN_MARGINS,
      editableClassName: Campaign.type,
      action,
      getExtraValidation,
    }),
    reachAndFrequencyStatus: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reachAndFrequencyStatus')),
      property: 'reach_and_frequency_status',
      type: TableColumnTypes.string,
      editablePropertyName: 'reachAndFrequencyStatus',
      source: () => [
        { id: ReachAndFrequencyStatus.COMPLETED, name: capitalize(i18n.global.t('states.completed')) },
        { id: ReachAndFrequencyStatus.IN_PROGRESS, name: capitalize(i18n.global.t('states.inProgress')) },
        { id: ReachAndFrequencyStatus.NOT_REQUESTED, name: capitalize(i18n.global.t('states.notRequested')) },
        { id: ReachAndFrequencyStatus.REQUESTED, name: capitalize(i18n.global.t('states.requested')) },
      ],
    }),
  };
};
